import { UNMATCHED_TERM_ID, useCoursesLoaderData } from "./coursesLoader";
import styles from "./TermInfo.module.scss";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";
import { useEffect } from "react";
import useVirtualStore from "../../common/virtual-store/useVirtualStore.tsx";
import { clsx } from "clsx";
import TermInfoRollup from "./TermInfoRollup.tsx";
import useProgramMetrics from "./useProgramMetrics.tsx";
import { logEvent } from "common/api/monitoring/index.ts";
import useTermInfoDiagnostics from "./useTermInfoDiagnostics.ts";
import MiscellaneousExclamationCircle from "common/icons/MiscellaneousExclamationCircle.tsx";
import Swimlane from "common/core-ui/Swimlane.tsx";
import MiscellaneousCalendar2 from "common/icons/MiscellaneousCalendar2.tsx";

export interface TermSwitcherOption {
  label: string;
  value: string | number;
}

export default function TermInfo() {
  const [searchParams, setSearchParams] = useSearchParams();
  const courseData = useCoursesLoaderData();
  const intl = useIntl();
  const { isVirtualStore } = useVirtualStore();
  const { numCourses } = useProgramMetrics();
  const unmatchedCoursesOption = intl.formatMessage({
    defaultMessage: "Other Courses",
  });
  const getUnmatchedCourseTitle = () => {
    return courseData.terms.length > 1
      ? unmatchedCoursesOption
      : intl.formatMessage({ defaultMessage: "All Courses" });
  };
  const getTitle = () => {
    return courseData.currentTerm
      ? courseData.currentTerm.displayName
      : getUnmatchedCourseTitle();
  };
  const getMaterialCount = () => {
    if (isVirtualStore) {
      // note smartling does not support multiple plurals in same string
      const coursesTxt = intl.formatMessage(
        {
          defaultMessage:
            "{numCourses, plural, =1 {1 Course} other {# Courses}}",
        },
        { numCourses }
      );
      const materialsTxt = intl.formatMessage(
        {
          defaultMessage:
            "{totalRequiredMaterials, plural, =1 {1 Required Course Material } other {# Required Course Materials}}",
        },
        { totalRequiredMaterials: courseData.learnerRequiredMaterials.length }
      );
      return intl.formatMessage(
        {
          defaultMessage: "{coursesTxt}, {materialsTxt}",
        },
        { coursesTxt, materialsTxt }
      );
    } else {
      return intl.formatMessage(
        {
          defaultMessage:
            "{materialsCount, plural, =1 {1 Course Material} other {# Course Materials}}",
        },
        { materialsCount: courseData.materialCount }
      );
    }
  };
  const options = courseData.terms.map<TermSwitcherOption>(
    ({ id, displayName }) => ({
      label: id === UNMATCHED_TERM_ID ? unmatchedCoursesOption : displayName,
      value: id,
    })
  );
  const getInitialValue = () => {
    const id = courseData.currentTerm?.id ?? UNMATCHED_TERM_ID;
    return options.find((option) => option.value === id);
  };

  useTermInfoDiagnostics();

  useEffect(() => {
    if (
      isVirtualStore &&
      courseData &&
      (courseData.materialCount === 0 || courseData.terms.length === 0)
    ) {
      logEvent("my_courses_no_terms_or_materials", {
        host: window.location.host,
        materialCount: courseData.materialCount,
        numTerms: courseData.terms.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVirtualStore, courseData.materialCount, courseData.terms.length]);

  return (
    <div className={styles.sizeContainer}>
      {courseData?.terms?.length > 1 && (
        <div className={styles.termSelectorContainer}>
          <div className={clsx("vst-icon-with-text", styles.termSelectorTitle)}>
            <MiscellaneousExclamationCircle />
            <FormattedMessage
              defaultMessage="<strong>Missing something?</strong> Check other terms or programs."
              values={{ strong: (chunks) => <strong>{chunks}</strong> }}
              tagName="span"
            />
          </div>
          <div className={styles.termSelector}>
            <Swimlane
              styleVariant="row"
              className={styles.termSelectorSwimlane}
              keepItemsReachable
              flexibleSize
            >
              {options.map((option) => (
                <li key={option.value}>
                  <BaseLinkOrButton
                    onClick={() => {
                      if (option.value !== getInitialValue()?.value) {
                        searchParams.set("termId", option.value.toString());
                        setSearchParams(searchParams);
                      }
                    }}
                    className={clsx(
                      "vst-button-rectangle",
                      "vst-button-size-small",
                      "vst-button-variant-outline",
                      styles.termSelectorButton,
                      getInitialValue()?.value === option.value &&
                        styles.termSelectorButtonActive
                    )}
                  >
                    <MiscellaneousCalendar2 height={14} />
                    {option.label}
                  </BaseLinkOrButton>
                </li>
              ))}
            </Swimlane>
          </div>
        </div>
      )}
      <div className={styles.container}>
        <div>
          <div className={styles.titleContainer}>
            <h1 className={styles.name}>{getTitle()}</h1>
          </div>
          <div className={styles.materialCount}>{getMaterialCount()}</div>
        </div>
        <TermInfoRollup />
      </div>
    </div>
  );
}
